// src/features/goal/components/GoalBreakdownPanel.tsx
// AI 拆解建議面板（GoalDetail 頁的獨立區塊）
//
// 位置：GoalTaskList 下方，樣式風格與 GoalTaskList 一致
// 生命週期：按下 AI 按鈕後出現 → 建議清單清空後自動消失 / 用戶關閉
// 互動：每條建議左側 + 按鈕，點了樂觀加入 task list，從建議清單移除

import React, { useState, useEffect, useRef } from 'react';
import { Sparkles, Loader2, X, Plus } from 'lucide-react';
import ObjectiveService, { GoalBreakdownTaskSuggestion } from '../services/objectiveService';
import { taskService } from '../../task';
import { Task, TaskCategory, TaskPriority, TaskEnergyLevel, OptimisticTask } from '../../../types/task';
import { useToast } from '../../../contexts/ToastContext';
import { logger } from '../../../utils/logger';
import { eventBus, EVENTS } from '../../../utils/eventBus';

interface GoalBreakdownPanelProps {
  objectiveId: string;
  userId: string; // 用於 optimistic task 建立，不送往後端
  onTaskAdded: (task: OptimisticTask) => void;
  onTaskAddFailed: (optimisticId: string) => void;
  onTaskCreated: (optimisticId: string, createdTask: Task) => void;
  onDismiss: () => void;
}

interface Suggestion extends GoalBreakdownTaskSuggestion {
  adding: boolean;  // 正在建立中（防重複點擊）
}

const GoalBreakdownPanel: React.FC<GoalBreakdownPanelProps> = ({
  objectiveId,
  userId,
  onTaskAdded,
  onTaskAddFailed,
  onTaskCreated,
  onDismiss,
}) => {
  const { showError } = useToast();

  // 用 ref 穩定 callbacks，避免每次 parent re-render 產生新 reference 觸發 effect
  const onTaskAddedRef = useRef(onTaskAdded);
  const onTaskAddFailedRef = useRef(onTaskAddFailed);
  const onTaskCreatedRef = useRef(onTaskCreated);
  const onDismissRef = useRef(onDismiss);
  useEffect(() => { onTaskAddedRef.current = onTaskAdded; }, [onTaskAdded]);
  useEffect(() => { onTaskAddFailedRef.current = onTaskAddFailed; }, [onTaskAddFailed]);
  useEffect(() => { onTaskCreatedRef.current = onTaskCreated; }, [onTaskCreated]);
  useEffect(() => { onDismissRef.current = onDismiss; }, [onDismiss]);

  const [phase, setPhase] = useState<'loading' | 'result' | 'error'>('loading');
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [errorMessage, setErrorMessage] = useState('');

  // fetchCount 控制手動重試（Try again），初始值 0 觸發第一次 fetch
  const [fetchCount, setFetchCount] = useState(0);

  useEffect(() => {
    let cancelled = false;

    setPhase('loading');
    setSuggestions([]);
    setErrorMessage('');

    ObjectiveService.breakdownGoalToTasks(objectiveId)
      .then((data) => {
        if (cancelled) return;
        setSuggestions(data.tasks.map((t) => ({ ...t, adding: false })));
        setPhase('result');
      })
      .catch((err) => {
        if (cancelled) return;
        logger.error('GoalBreakdownPanel: fetch failed', err);
        setErrorMessage(err instanceof Error ? err.message : 'Failed to generate suggestions.');
        setPhase('error');
      });

    return () => { cancelled = true; };
  }, [fetchCount, objectiveId]);


  async function handleAdd(idx: number) {
    const s = suggestions[idx];
    if (s.adding) return;

    // 樂觀 task（暫時 id，讓 GoalDetail 先顯示）
    const optimisticTask: OptimisticTask = {
      id: `optimistic-${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
      title: s.title,
      status: 'OPEN',
      priority: (s.priority as TaskPriority) ?? '2',
      userId,
      objectiveId,
      createdAt: new Date(),
      updatedAt: new Date(),
      migrationCount: 0,
    };

    // 先告知父層樂觀加入，從建議清單移除
    onTaskAddedRef.current(optimisticTask);
    const remaining = suggestions.filter((_, i) => i !== idx);
    setSuggestions(remaining);

    // 背景建立 API（不論清單是否清空都必須完成寫入）
    try {
      const createdTask = await taskService.createTask({
        title: s.title,
        priority: s.priority as TaskPriority,
        notes: s.notes,
        category: s.category as TaskCategory | undefined,
        energyLevel: s.energyLevel as TaskEnergyLevel | undefined,
        objectiveId,
        userId,
      });
      // 寫入成功，用真實 task 替換 optimistic task（避免觸發 loadTasks 的大 spinner）
      onTaskCreatedRef.current(optimisticTask.id, createdTask);
      // 通知 Lesson 3 Guide 完成條件達成
      eventBus.publish(EVENTS.LESSON.GOAL_TASK_CREATED);
      // 清單清空後關閉面板（僅在成功路徑執行）
      if (remaining.length === 0) {
        onDismissRef.current();
      }
    } catch (err) {
      logger.error('GoalBreakdownPanel: create task failed', err);
      showError('Failed to add task. Please try again.');
      // 回滾：通知父層移除 optimistic task，並把建議放回清單
      onTaskAddFailedRef.current(optimisticTask.id);
      setSuggestions((prev) => [...prev, { ...s, adding: false }]);
    }
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 pt-3 pb-2">
        <div className="flex items-center gap-2">
          <Sparkles size={15} className="text-teal-500" />
          <span className="text-sm font-medium text-gray-700">Suggestions</span>
        </div>
        <button
          onClick={onDismiss}
          aria-label="Dismiss suggestions"
          className="text-gray-400 hover:text-gray-600 transition-colors p-0.5 rounded"
        >
          <X size={15} />
        </button>
      </div>

      {/* Body */}
      <div className="px-4 pb-3">
        {/* Loading */}
        {phase === 'loading' && (
          <div className="flex items-center gap-3 py-4">
            <Loader2 size={16} className="text-teal-500 animate-spin flex-shrink-0" />
            <span className="text-sm text-gray-400">Thinking about how to break this down...</span>
          </div>
        )}

        {/* Error */}
        {phase === 'error' && (
          <div className="py-3 space-y-3">
            <p className="text-sm text-red-500">{errorMessage}</p>
            <button onClick={() => setFetchCount((n) => n + 1)} className="btn-secondary text-sm px-3 py-1.5">
              Try again
            </button>
          </div>
        )}

        {/* Result */}
        {phase === 'result' && (
          <div className="space-y-1">
            {suggestions.map((s, idx) => (
              <div key={s.title} className="flex items-start gap-3 py-1.5">
                {/* Add button */}
                <button
                  onClick={() => handleAdd(idx)}
                  disabled={s.adding}
                  aria-label={`Add "${s.title}"`}
                  data-lesson-anchor={idx === 0 ? 'goal-add-task' : undefined}
                  className="flex-shrink-0 text-teal-500 hover:text-teal-700 transition-colors disabled:opacity-40 mt-0.5"
                >
                  {s.adding
                    ? <Loader2 size={15} className="animate-spin" />
                    : <Plus size={15} strokeWidth={2} />
                  }
                </button>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-900 truncate">{s.title}</p>
                  {s.notes && (
                    <p className="text-xs text-gray-400 mt-0.5 line-clamp-1">{s.notes}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default GoalBreakdownPanel;
